/**
 * Shared constants for Expogreens.
 * Import from frontend or backend:
 *   import { FIXED_VARIETIES, BOX_SIZES } from '@expogreens/shared'
 *
 * Having these here eliminates the duplication between frontend and backend
 * detected in the previous audit.
 */

// Rate used to convert USD to COP in the UI/reports.
export const USD_TO_COP = 4200

// Catálogo de variedades.
//
// Vacío a propósito: el catálogo lo crea el cliente (POST /api/varieties,
// se guardan en data.customVarieties) y "borrar todo" lo deja en cero. El
// listado público (GET /api/varieties) es la unión de las variedades custom
// + las que aparezcan en inventario. Antes había una lista fija hardcodeada
// que no se podía borrar; se retiró por pedido del cliente.
export const FIXED_VARIETIES = []

// Box size catalog. Keys are persisted in the DB; values are labels for UI.
export const BOX_SIZES = {
  fullbox: 'Full Box',
  halfbox: 'Half Box',
  quarterbox: 'Quarter Box',
  oneeightbox: '1/8 Box',
}

// Catálogo de combos.
//
// Vacío a propósito: el cliente crea sus propios combos desde el módulo
// Producto (POST /api/combos → data.customCombos, con isCustom=true) y
// "borrar todo" los elimina. GET /api/combos devuelve la unión de este
// array (vacío) + los custom, así que en la práctica = solo los del cliente.
//
// `defaultPacking` (en cada combo custom) es la receta de empaque por
// defecto — un ARRAY de cajas; `normalizeDefaultPacking()` acepta también
// el formato legacy de objeto único y lo convierte.
export const PREDEFINED_COMBOS = []

/**
 * Convierte `defaultPacking` en su forma canónica (array de cajas).
 *
 * Acepta:
 *   - undefined  → [] (sin empaque por defecto)
 *   - array      → se sanitiza
 *   - objeto { boxSize, boxCount } (formato legacy) → se convierte en
 *     [{ boxSize, boxCount, items: comboItems }] para no perder datos.
 *
 * Cada caja se normaliza con boxSize válido, boxCount ≥ 1 y items[]
 * filtrados a entradas con variedad y cantidad > 0.
 */
export function normalizeDefaultPacking(raw, comboItems = []) {
  // boxSizes válidos viven en este archivo; lo redeclaro acá para no
  // crear un require circular con el normalizador.
  const validBoxSize = (s) =>
    s === 'fullbox' || s === 'halfbox' || s === 'quarterbox' || s === 'oneeightbox'
  const sanitizeItems = (arr) =>
    (Array.isArray(arr) ? arr : [])
      .map((it) => ({
        variety: String(it?.variety || ''),
        stemLength: Number(it?.stemLength) || 0,
        quantity: Number(it?.quantity) || 0,
      }))
      .filter((it) => it.variety && it.quantity > 0)
  const sanitizeBox = (b) => ({
    boxSize: validBoxSize(b?.boxSize) ? b.boxSize : 'fullbox',
    boxCount: Math.max(1, Number(b?.boxCount) || 1),
    items: sanitizeItems(b?.items),
  })
  if (Array.isArray(raw)) {
    return raw.map(sanitizeBox).filter((b) => b.items.length > 0)
  }
  if (raw && typeof raw === 'object') {
    // Legacy: un único {boxSize, boxCount} → caja con TODOS los items.
    return [
      sanitizeBox({
        boxSize: raw.boxSize,
        boxCount: raw.boxCount,
        items: comboItems,
      }),
    ].filter((b) => b.items.length > 0)
  }
  return []
}

// Partner types used by the Partners section.
// Alineado con Expogreens-main (src/app/utils/fakeData.ts):
//   customs_agency → Agencia de Aduanas
//   supplier       → Proveedor
//   airline        → Aerolínea   (con acento)
export const PARTNER_TYPES = {
  customs_agency: 'Agencia de Aduanas',
  supplier: 'Proveedor',
  airline: 'Aerolínea',
}

// Canonical sale lifecycle (per feedback doc, Dashboard module).
//
//   confirmed   → auto al crear la venta
//   supplying   → "En abastecimiento": auto cuando se crea una OC ligada
//   processing  → "En procesamiento": auto cuando se recibe producto (PO at_farm)
//   dispatched  → "Despachado": manual desde el dashboard; requiere inventario
//                 suficiente en la etapa `cold_room` (Procesado).
//   delivered   → "Entregado": manual desde el dashboard (confirmación cliente).
//   cancelled   → terminal manual.
//
// Se conservan en la lista de validación los valores legados `pending` y
// `in_preparation` solo para que datos viejos (seed v1) sigan siendo
// leíbles. SALE_STATUS_LEGACY_MAP define cómo mostrarlos en UI: pending
// se equipara a `confirmed` e `in_preparation` a `processing`.
export const SALE_STATUSES = [
  'confirmed',
  'supplying',
  'processing',
  'dispatched',
  'delivered',
  'cancelled',
  // legacy values - aceptados en validación pero migrados visualmente
  'pending',
  'in_preparation',
]
export const SALE_STATUS_LABELS = {
  confirmed: 'Confirmado',
  supplying: 'En abastecimiento',
  processing: 'En procesamiento',
  dispatched: 'Despachado',
  delivered: 'Entregado',
  cancelled: 'Cancelada',
  // legacy → se muestran con el label nuevo equivalente
  pending: 'Confirmado',
  in_preparation: 'En procesamiento',
}
export const SALE_STATUS_LEGACY_MAP = {
  pending: 'confirmed',
  in_preparation: 'processing',
}
// Estados manuales (transiciones desde el dashboard).
export const SALE_STATUS_MANUAL = ['dispatched', 'delivered', 'cancelled']
// Estados que cuentan como "venta abierta" (no finalizada / no cancelada).
export const SALE_OPEN_STATUSES = [
  'confirmed',
  'supplying',
  'processing',
  // legacy
  'pending',
  'in_preparation',
]

export const PO_STATUSES = ['pending', 'ordered', 'confirmed', 'at_farm', 'completed', 'cancelled']

// Inventory stages (chose a single canonical vocabulary; eliminates the
// frontend/backend mismatch detected in the previous audit).
//
// Nota: el doc de feedback pide renombrar "Cuarto Frío" a "Procesado" y
// eliminar "En proceso" de la UI. Por ahora cambiamos solo el label
// para no romper datos persistidos; los keys siguen siendo los mismos.
export const INVENTORY_STAGES = ['unprocessed', 'in_process', 'cold_room']
export const INVENTORY_STAGE_LABELS = {
  unprocessed: 'Sin procesar',
  in_process: 'En proceso',
  cold_room: 'Procesado',
}

// Client types accepted by the Clients form.
//
// Por feedback se reducen las categorías a SOLO dos:
//   principal   → cliente principal (puede tener consignatarios ligados)
//   consignee   → consignatario / ubicación de un cliente principal
//
// Los valores legados (direct / distributor / dropshipping / wholesale) se
// mantienen en el mapa para que datos viejos sigan siendo leíbles, pero
// la UI los rotula como "Principal" o "Consignatario" según corresponda.
export const CLIENT_TYPES = {
  principal: 'Principal',
  consignee: 'Consignatario',
  // legacy
  direct: 'Principal',
  distributor: 'Principal',
  wholesale: 'Principal',
  dropshipping: 'Consignatario',
}
// Conjunto canónico para los selects de la UI.
export const CLIENT_TYPES_CANONICAL = ['principal', 'consignee']
export const CLIENT_TYPE_LEGACY_MAP = {
  direct: 'principal',
  distributor: 'principal',
  wholesale: 'principal',
  dropshipping: 'consignee',
}

// Purchase priority buckets used by the Purchases "pending to buy" board.
//
// Labels renombrados por feedback del cliente — el board ahora agrupa por
// ventana de despacho, no por "prioridad" abstracta:
//   urgent    → Despachos de hoy y/o mañana
//   medium    → Despachos de la semana (no hoy ni mañana)
//   long_term → Despachos de la próxima semana o en adelante
export const PURCHASE_PRIORITIES = ['urgent', 'medium', 'long_term']
export const PURCHASE_PRIORITY_LABELS = {
  urgent: 'Despachos de hoy y/o mañana',
  medium: 'Despachos de la semana',
  long_term: 'Despachos de la próxima semana o en adelante',
}

// Monedas aceptadas en una OC. Por defecto COP (cliente reportó que la
// mayoría de proveedores facturan en COP/tallo y solo algunos en USD).
export const PURCHASE_CURRENCIES = ['COP', 'USD']

// Return lifecycle.
//
//   requested  -> recien creada (esperando recoleccion / recepcion)
//   in_review  -> recibida, equipo evalua producto y cantidades
//   confirmed  -> estado / cantidades / tipo de resolucion confirmados
//   resolved   -> aplicado al inventario (restock, restar, composted...)
//   composted  -> descartada por compostaje (>5 dias sin recoger)
//   cancelled  -> anulada manualmente
export const RETURN_STATUSES = [
  'requested',
  'in_review',
  'confirmed',
  'resolved',
  'composted',
  'cancelled',
]
export const RETURN_STATUS_LABELS = {
  requested: 'Solicitada',
  in_review: 'En revision',
  confirmed: 'Confirmada',
  resolved: 'Resuelta',
  composted: 'Compostada',
  cancelled: 'Cancelada',
}

// Origin: la devolucion puede ser contra una orden de compra (proveedor) o
// contra una orden de venta (cliente).
export const RETURN_TYPES = ['purchase', 'sale']
export const RETURN_TYPE_LABELS = {
  purchase: 'De compra',
  sale: 'De venta',
}

// Resolution determines how the inventory is adjusted when the return moves
// to `resolved`. The sign is applied to the confirmed quantity per stage.
//
//   restock_cold     -> reingreso a cold_room (+)
//   restock_process  -> reingreso a in_process (+)
//   restock_raw      -> reingreso a unprocessed (+)
//   discard          -> descarte, no mueve inventario (0)
//   composted        -> compostado, no mueve inventario (0)
//   deduct           -> salida adicional del inventario (-)
//
// El frontend elige el default segun el tipo (purchase -> deduct,
// sale -> restock_cold) pero el operador puede cambiarlo al confirmar.
export const RETURN_RESOLUTIONS = {
  restock_cold: { label: 'Reingresar a cuarto frio', sign: +1, stage: 'cold_room' },
  restock_process: { label: 'Reingresar a en proceso', sign: +1, stage: 'in_process' },
  restock_raw: { label: 'Reingresar a sin procesar', sign: +1, stage: 'unprocessed' },
  discard: { label: 'Descartar', sign: 0, stage: null },
  composted: { label: 'Compostado', sign: 0, stage: null },
  deduct: { label: 'Salida del inventario', sign: -1, stage: 'unprocessed' },
}

// Despues de este numero de dias sin recoger, la devolucion se puede compostar.
export const RETURN_COMPOST_AFTER_DAYS = 5

// Motivos de devolución — catálogo cerrado para que el operario los
// seleccione de una lista en vez de escribir texto libre. Esto mejora la
// trazabilidad (reportes agrupables por motivo) y la coherencia.
//
// El campo `severity` orienta la resolución típica:
//   'high'   → producto descartable (descarte/composted)
//   'medium' → producto recuperable parcialmente
//   'low'    → producto recuperable (puede reingresarse a inventario)
export const RETURN_REASONS = [
  { id: 'broken_stems', label: 'Tallos quebrados o partidos', severity: 'high' },
  { id: 'dehydrated', label: 'Producto deshidratado', severity: 'high' },
  { id: 'fungus_mold', label: 'Hongos o moho', severity: 'high' },
  { id: 'pests_insects', label: 'Insectos o plagas', severity: 'high' },
  { id: 'low_quality', label: 'Calidad o color inferior a lo esperado', severity: 'medium' },
  { id: 'wrong_length', label: 'Longitud de tallo incorrecta', severity: 'medium' },
  { id: 'damaged_in_transit', label: 'Producto golpeado o maltratado en transporte', severity: 'medium' },
  { id: 'dirty_product', label: 'Producto sucio o con residuos', severity: 'low' },
  { id: 'late_arrival', label: 'Llegada tardía — pérdida de frescura', severity: 'medium' },
  { id: 'wrong_variety', label: 'Variedad o especificación equivocada', severity: 'high' },
]
export const RETURN_REASON_LABELS = RETURN_REASONS.reduce((acc, r) => {
  acc[r.id] = r.label
  return acc
}, {})
